import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { useAuth } from '../store/auth';
import {
  LayoutGrid,
  BarChart3,
  Target,
  MousePointer,
  Megaphone,
  Gift,
  Users,
  UserCircle,
  Globe,
  Send,
  ScrollText,
  Settings,
  LogOut,
  DollarSign,
  Link2,
  Shield,
  Menu,
  X,
} from 'lucide-react';
import { ThemeSwitcher } from './ThemeSwitcher';
import { Badge } from './ui/Badge';

// `superOnly: true` hides this item from regular admins. Only super-admin
// (users.is_super_admin = 1) sees Forwarding / Postback Logs / Audit Log
// because they expose partner-confidential data or surveil other admins'
// activity. Backend also enforces — frontend hiding is UX, not security.
const adminMenu: Array<{
  to: string;
  label: string;
  icon: typeof LayoutGrid;
  superOnly?: boolean;
}> = [
  { to: '/overviews', label: 'Overviews', icon: LayoutGrid },
  { to: '/statistics', label: 'Statistics', icon: BarChart3 },
  { to: '/conversions', label: 'Conversions', icon: Target },
  { to: '/clicks', label: 'Clicks', icon: MousePointer },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone },
  { to: '/offers', label: 'Offers', icon: Gift },
  { to: '/publishers', label: 'Sub-Publishers', icon: UserCircle },
  { to: '/tracker-domains', label: 'Domains', icon: Globe },
  { to: '/forwarding', label: 'Forwarding', icon: Send, superOnly: true },
  { to: '/postback-logs', label: 'Postbacks', icon: ScrollText, superOnly: true },
  { to: '/audit-log', label: 'Audit Log', icon: Shield, superOnly: true },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const userMenu = [
  { to: '/overviews', label: 'Overviews', icon: LayoutGrid },
  { to: '/statistics', label: 'Statistics', icon: BarChart3 },
  { to: '/conversions', label: 'Conversions', icon: Target },
  { to: '/clicks', label: 'Clicks', icon: MousePointer },
  { to: '/tracking-links', label: 'Tracking Links', icon: Link2 },
  { to: '/earnings', label: 'My Earnings', icon: DollarSign },
];

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Filter out super-only items for regular admins. user-role doesn't see
  // adminMenu at all so the predicate is always true for it.
  const menu = (user?.role === 'admin' ? adminMenu : userMenu).filter(
    (item) => !('superOnly' in item && item.superOnly) || user?.isSuperAdmin,
  );

  // D13: Clock ticks every 30s so the displayed time stays current.
  // Initial value from Date.now() so there's no blank flash on mount.
  const [clockTime, setClockTime] = useState(() =>
    new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  );

  // Online-users widget — polls every 5 MINUTES. User explicitly flagged
  // this as low-priority ("tidak terlalu penting cuma butuh tau saja
  // berapa yang online"), so cadence is matched exactly to the server-side
  // 5-min activity window. Polling more often returns identical data; less
  // often risks missing the entire window.
  //
  // Cost: 100 users × 12 polls/hour × 24h × 30 days = 864K req/month.
  // Effectively free — under 0.01% of Workers Paid Plan headroom.
  const onlineQuery = useQuery({
    queryKey: ['online-count'],
    queryFn: () =>
      apiClient.get<{ total: number; admins: number; publishers: number }>(
        '/api/v1/auth/online-count',
      ),
    refetchInterval: 300_000,
    refetchIntervalInBackground: false,
    enabled: !!user,
  });
  useEffect(() => {
    const id = setInterval(() => {
      setClockTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }));
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  // Mobile sidebar drawer state. Closed by default — opens via hamburger and
  // auto-closes when route changes (so tapping a menu item navigates AND
  // closes the overlay simultaneously, no extra interaction needed).
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock page scroll when drawer is open so user doesn't accidentally scroll
  // the underlying content while picking a menu item on mobile.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  return (
    // BUGFIX: was `min-h-screen` which lets the flex container grow beyond
    // the viewport, causing the body to scroll and dragging the `lg:static`
    // sidebar along with it. User wants sidebar to stay sticky at the top
    // of viewport. Switched to `h-screen overflow-hidden` so the root is
    // EXACTLY viewport-tall — only the inner `overflow-y-auto` regions
    // (sidebar nav + main page content) scroll. Sidebar header/footer stay
    // visually anchored.
    <div className="h-screen overflow-hidden flex">
      {/* Sidebar — fixed-position on mobile (slide-in drawer), static on lg+
          via lg:static. The lg:translate-x-0 override keeps it always-visible
          on desktop regardless of mobileOpen state. */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 lg:w-60 shrink-0 cyber-panel rounded-none border-r border-border-subtle border-t-0 border-l-0 border-b-0 flex flex-col transform transition-transform duration-200 ease-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        aria-label="Main navigation"
      >
        <div className="p-5 border-b border-border-subtle flex items-center justify-between gap-2">
          <NavLink to="/" className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded bg-neon-cyan/10 border border-neon-cyan flex items-center justify-center text-neon-cyan font-display font-bold shrink-0">
              RJ
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-sm tracking-widest text-text-primary">
                RJ <span className="text-neon-cyan">TRACK</span>
              </div>
              <div className="text-[9px] text-text-tertiary uppercase tracking-widest">
                v0.1 alpha
              </div>
            </div>
          </NavLink>
          {/* Close button — only on mobile drawer */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-text-tertiary hover:text-text-primary p-1"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {menu.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/30 shadow-neon-cyan'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary border border-transparent'
                }`
              }
            >
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border-subtle space-y-2">
          <div className="flex items-center gap-3 p-2 rounded bg-bg-tertiary">
            <div className="w-8 h-8 rounded bg-neon-magenta/10 border border-neon-magenta/40 flex items-center justify-center text-neon-magenta font-display font-bold text-xs uppercase">
              {user?.username?.[0] ?? '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.displayName ?? user?.username}</div>
              <Badge variant={user?.role === 'admin' ? 'info' : 'live'}>{user?.role}</Badge>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="text-text-tertiary hover:text-neon-red transition-colors"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
          <NavLink
            to="/profile"
            className="flex items-center gap-2 px-2 py-1.5 text-xs text-text-tertiary hover:text-neon-cyan transition-colors"
          >
            <Settings size={12} /> Profile & Theme
          </NavLink>
        </div>
      </aside>

      {/* Mobile-only backdrop — tap to close drawer */}
      {mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          aria-label="Close menu overlay"
        />
      )}

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header
          data-app-header
          className="h-14 px-3 sm:px-6 flex items-center justify-between gap-2 border-b border-border-subtle bg-bg-primary/50 backdrop-blur"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Hamburger — only visible on mobile */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-text-secondary hover:text-text-primary p-1.5 -ml-1.5 border border-border-subtle rounded"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
            <div className="pulse-dot shrink-0" />
            <span className="text-xs font-mono text-text-secondary uppercase tracking-wider truncate">
              Live · {clockTime}
            </span>
            {onlineQuery.data && (
              <span
                className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-neon-cyan/30 bg-neon-cyan/5 text-neon-cyan rounded-sm"
                title={
                  user?.role === 'admin'
                    ? `${onlineQuery.data.admins} admin · ${onlineQuery.data.publishers} publisher (active in last 5 min)`
                    : `${onlineQuery.data.total} users active in last 5 min`
                }
              >
                <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse" />
                {onlineQuery.data.total} online
              </span>
            )}
          </div>
          <ThemeSwitcher />
        </header>
        <div className="flex-1 p-3 sm:p-6 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
